import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Context for subscription access
const SubscriptionAccessContext = createContext(null);

// Cache for module access to avoid repeated API calls
let accessCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

/**
 * Hook to check module access based on user's subscription plan
 * 
 * This is different from useModuleAccess which checks admin-configured module status.
 * This hook checks if a user's subscription plan grants access to specific features.
 */
export const useSubscriptionAccess = () => {
  const { user, isAuthenticated, authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessData, setAccessData] = useState({
    isPaid: false,
    moduleAccess: {
      practice_sessions: false,
      progress_tracker: false,
      notebook: false,
      exam_insights: false
    },
    moduleAccessOverride: false,
    planName: null,
    message: null
  });

  const fetchAccess = useCallback(async (force = false) => {
    // Wait for AuthContext to finish hydrating /auth/me before deciding
    // "not authenticated" — otherwise a page-refresh with a valid token in
    // localStorage would briefly (and stickily on some pages) report the
    // user as logged-out and lock all paid features.
    if (authLoading) {
      return;
    }

    // If not authenticated, set defaults and return
    if (!isAuthenticated || !isAuthenticated()) {
      setAccessData({
        isPaid: false,
        moduleAccess: {
          practice_sessions: false,
          progress_tracker: false,
          notebook: false,
          exam_insights: false
        },
        moduleAccessOverride: false,
        planName: null,
        message: 'Please login to access premium features'
      });
      setLoading(false);
      return;
    }

    // Use cache if available and not expired
    if (!force && accessCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      setAccessData(accessCache);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/user/module-access`);
      const data = response.data;
      
      const newAccessData = {
        isPaid: data.is_paid || false,
        moduleAccess: data.module_access || {
          practice_sessions: false,
          progress_tracker: false,
          notebook: false,
          exam_insights: false,
          live_classes: false
        },
        moduleAccessOverride: data.module_access_override || false,
        planName: data.plan_name || null,
        subscribedGrade: data.subscribed_grade || null,
        message: data.message || null
      };
      
      // Update cache
      accessCache = newAccessData;
      cacheTimestamp = Date.now();
      
      setAccessData(newAccessData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch subscription access:', err);
      setError(err);
      // On error, default to no access
      setAccessData({
        isPaid: false,
        moduleAccess: {
          practice_sessions: false,
          progress_tracker: false,
          notebook: false,
          exam_insights: false
        },
        moduleAccessOverride: false,
        planName: null,
        message: 'Failed to verify subscription'
      });
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess, user?.id, authLoading]);

  /**
   * Check if user has access to a specific module
   * @param {string} moduleKey - practice_sessions | progress_tracker | notebook | exam_insights
   * @returns {boolean}
   */
  const hasModuleAccess = useCallback((moduleKey) => {
    // Admin override grants all access
    if (accessData.moduleAccessOverride) return true;
    // Check specific module
    return accessData.moduleAccess[moduleKey] === true;
  }, [accessData]);

  /**
   * Check if user can access Practice Sessions
   */
  const canAccessPracticeSessions = useCallback(() => {
    return hasModuleAccess('practice_sessions');
  }, [hasModuleAccess]);

  /**
   * Check if user can access Progress Tracker
   */
  const canAccessProgressTracker = useCallback(() => {
    return hasModuleAccess('progress_tracker');
  }, [hasModuleAccess]);

  /**
   * Check if user can access Notebook
   */
  const canAccessNotebook = useCallback(() => {
    return hasModuleAccess('notebook');
  }, [hasModuleAccess]);

  /**
   * Check if user can access Exam Insights
   */
  const canAccessExamInsights = useCallback(() => {
    return hasModuleAccess('exam_insights');
  }, [hasModuleAccess]);

  /**
   * Check if user can access Live Classes
   */
  const canAccessLiveClasses = useCallback(() => {
    return hasModuleAccess('live_classes');
  }, [hasModuleAccess]);

  /**
   * Refresh access data (clear cache and re-fetch)
   */
  const refresh = useCallback(() => {
    accessCache = null;
    cacheTimestamp = null;
    fetchAccess(true);
  }, [fetchAccess]);

  /**
   * Clear cache (useful when user upgrades plan)
   */
  const clearCache = useCallback(() => {
    accessCache = null;
    cacheTimestamp = null;
  }, []);

  return {
    loading,
    error,
    isPaid: accessData.isPaid,
    moduleAccess: accessData.moduleAccess,
    moduleAccessOverride: accessData.moduleAccessOverride,
    planName: accessData.planName,
    subscribedGrade: accessData.subscribedGrade,
    message: accessData.message,
    hasModuleAccess,
    canAccessPracticeSessions,
    canAccessProgressTracker,
    canAccessNotebook,
    canAccessExamInsights,
    canAccessLiveClasses,
    refresh,
    clearCache
  };
};

/**
 * Provider component for subscription access context
 */
export const SubscriptionAccessProvider = ({ children }) => {
  const access = useSubscriptionAccess();
  
  return (
    <SubscriptionAccessContext.Provider value={access}>
      {children}
    </SubscriptionAccessContext.Provider>
  );
};

/**
 * Hook to use subscription access from context
 */
export const useSubscriptionAccessContext = () => {
  const context = useContext(SubscriptionAccessContext);
  if (!context) {
    throw new Error('useSubscriptionAccessContext must be used within SubscriptionAccessProvider');
  }
  return context;
};

export default useSubscriptionAccess;
